// sessionBasedOdooService.js - Enhanced with detailed debugging

class OdooSessionService {
  constructor(config) {
    this.baseUrl = config.url; // https://mimir.xyz
    this.db = config.db; // production
    this.login = config.login; // salasar.developer@icloud.com
    this.password = config.password;
    this.isAuthenticated = false;
    this.sessionId = null;
    this.uid = null;
  }

  async authenticate() {
    if (this.isAuthenticated && this.uid) {
      console.log('Already authenticated, UID:', this.uid);
      return true;
    }

    try {
      console.log('🔐 Starting authentication process...');
      console.log('Config:', { 
        baseUrl: this.baseUrl, 
        db: this.db, 
        login: this.login 
      });
      
      const response = await fetch(`${this.baseUrl}/web/session/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important: Include cookies
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'call',
          params: {
            db: this.db,
            login: this.login,
            password: this.password
          }
        })
      });

      console.log('🌐 Authentication response status:', response.status);
      console.log('🌐 Authentication response headers:', [...response.headers.entries()]);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Full authentication response:', JSON.stringify(data, null, 2));

      if (data.error) {
        console.error('❌ Authentication error from server:', data.error);
        throw new Error(`Authentication failed: ${data.error.message}`);
      }

      if (data.result) {
        console.log('✅ Authentication result:', data.result);
        
        if (data.result.uid) {
          this.isAuthenticated = true;
          this.uid = data.result.uid;
          this.sessionId = data.result.session_id;
          console.log('🎉 Authentication successful!');
          console.log('   UID:', this.uid);
          console.log('   Session ID:', this.sessionId);
          return true;
        } else {
          console.error('❌ No UID in response');
          throw new Error('Authentication failed: No UID received');
        }
      } else {
        console.error('❌ No result in response');
        throw new Error('Authentication failed: Invalid response format');
      }

    } catch (error) {
      console.error('💥 Authentication error:', error);
      this.isAuthenticated = false;
      this.uid = null;
      this.sessionId = null;
      throw error;
    }
  }

  async getSessionInfo() {
    try {
      console.log('📋 Getting session info...');
      await this.authenticate();

      const response = await fetch(`${this.baseUrl}/web/session/get_session_info`, {
        method: 'GET',
        credentials: 'include'
      });

      console.log('🌐 Session info response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Session info response:', JSON.stringify(data, null, 2));
      return data;

    } catch (error) {
      console.error('💥 Get session info error:', error);
      throw error;
    }
  }

  async fetchProducts() {
    try {
      console.log('📦 Starting product fetch...');
      
      // Ensure we're authenticated first
      const isAuth = await this.authenticate();
      if (!isAuth) {
        throw new Error('Authentication failed before fetching products');
      }

      console.log('📦 Fetching products from custom endpoint...');
      console.log('📦 Endpoint URL:', `${this.baseUrl}/my_library/productsjson`);
      
      const response = await fetch(`${this.baseUrl}/my_library/productsjson`, {
        method: 'GET',
        credentials: 'include', // Include session cookies
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      console.log('🌐 Products response status:', response.status);
      console.log('🌐 Products response headers:', [...response.headers.entries()]);

      if (!response.ok) {
        console.error('❌ Products fetch failed with status:', response.status);
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Raw products data received:');
      console.log('   Type:', typeof data);
      console.log('   Is Array:', Array.isArray(data));
      console.log('   Length/Keys:', Array.isArray(data) ? data.length : Object.keys(data));
      console.log('   Full data:', JSON.stringify(data, null, 2));
      
      return data;

    } catch (error) {
      console.error('💥 Fetch products error:', error);
      
      // If it's an auth error, reset authentication state
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        console.log('🔄 Resetting authentication due to 401 error');
        this.isAuthenticated = false;
        this.uid = null;
        this.sessionId = null;
      }
      
      throw error;
    }
  }

  // Generic method for making authenticated requests to any endpoint
  async makeAuthenticatedRequest(endpoint, options = {}) {
    try {
      console.log(`🌐 Making authenticated request to: ${endpoint}`);
      
      await this.authenticate();

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        }
      });

      console.log(`🌐 Response status for ${endpoint}:`, response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Request failed for ${endpoint}:`, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`📊 Response data for ${endpoint}:`, data);
      return data;

    } catch (error) {
      console.error(`💥 Request to ${endpoint} failed:`, error);
      throw error;
    }
  }

  // Test connectivity method
  async testConnection() {
    try {
      console.log('🔍 Testing basic connectivity...');
      
      const response = await fetch(`${this.baseUrl}/web/database/selector`, {
        method: 'GET',
        credentials: 'include'
      });

      console.log('🌐 Connection test status:', response.status);
      return response.status === 200;
      
    } catch (error) {
      console.error('💥 Connection test failed:', error);
      return false;
    }
  }

  // Logout method
  async logout() {
    try {
      console.log('🚪 Logging out...');
      
      const response = await fetch(`${this.baseUrl}/web/session/destroy`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'call',
          params: {}
        })
      });

      this.isAuthenticated = false;
      this.uid = null;
      this.sessionId = null;
      
      console.log('✅ Logged out successfully');
      return true;

    } catch (error) {
      console.error('💥 Logout error:', error);
      return false;
    }
  }

  // Helper method to check authentication status
  getAuthStatus() {
    return {
      isAuthenticated: this.isAuthenticated,
      uid: this.uid,
      sessionId: this.sessionId
    };
  }
}

export default OdooSessionService;